interface CognitoConfig {
  userPoolId: string
  userPoolClientId: string
  identityPoolId: string
  region: string
}

interface ApiConfig {
  endpoint: string
  region: string
}

interface websocketConfig {
  url: string
}

interface UrlsConfig {
  adminUrl: string
  userUrl: string
}

interface AwsConfig {
  Auth: {
    Cognito: {
      userPoolId: string
      userPoolClientId: string
      identityPoolId: string
    }
  }
  region: string
}

interface AppConfig {
  cognito: CognitoConfig
  api: ApiConfig
  websocket?: websocketConfig
  [key: string]: unknown
}

// Fallback static config for development/testing
function getFallbackCognitoConfig(): CognitoConfig | undefined {
  const authConfig: CognitoConfig = {
    userPoolId: import.meta.env.VITE_LOCAL_COGNITO_USER_POOL_ID,
    userPoolClientId: import.meta.env.VITE_LOCAL_COGNITO_USER_POOL_CLIENT_ID,
    identityPoolId: import.meta.env.VITE_LOCAL_COGNITO_IDENTITY_POOL_ID,
    region: import.meta.env.VITE_LOCAL_COGNITO_REGION,
  }

  if (authConfig.userPoolId && authConfig.userPoolClientId && authConfig.identityPoolId && authConfig.region) {
    return authConfig
  } else {
    console.warn('AWS Cognito local configuration is incomplete. Please set the VITE_LOCAL_COGNITO_* environment variables for development/testing.')
  }

  return undefined
}

export function getCognitoConfig(): CognitoConfig | undefined {
  const authConfig: CognitoConfig = {
    userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
    userPoolClientId: import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID,
    identityPoolId: import.meta.env.VITE_COGNITO_IDENTITY_POOL_ID,
    region: import.meta.env.VITE_COGNITO_REGION,
  }

  if (authConfig.userPoolId && authConfig.userPoolClientId && authConfig.identityPoolId && authConfig.region) {
    return authConfig
  } else {
    console.warn('AWS Cognito configuration is incomplete. Falling back to environment variables if available.')
  }

  return getFallbackCognitoConfig()
}

function getLocalApiConfig(): ApiConfig | undefined {
  const apiConfig: ApiConfig = {
    endpoint: import.meta.env.VITE_API_ENDPOINT,
    region: import.meta.env.VITE_API_REGION,
  }

  if (apiConfig.endpoint && apiConfig.region) {
    return apiConfig
  } else {
    console.warn(
      'API local configuration is incomplete. Please set VITE_API_ENDPOINT and VITE_API_REGION environment variables for development/testing.',
    )
  }

  return undefined
}

export function getApiConfig(): ApiConfig | undefined {
  const apiConfig: ApiConfig = {
    endpoint: import.meta.env.VITE_API_ENDPOINT,
    region: import.meta.env.VITE_API_REGION,
  }

  if (apiConfig.endpoint && apiConfig.region) {
    return apiConfig
  } else {
    console.warn('API configuration is incomplete. Please set VITE_API_ENDPOINT and VITE_API_REGION environment variables.')
  }

  return getLocalApiConfig()
}

function getFallbackWebsocketConfig(): websocketConfig | undefined {
  const wsConfig: websocketConfig = {
    url: import.meta.env.VITE_LOCAL_WEBSOCKET_URL,
  }

  if (wsConfig.url) {
    return wsConfig
  } else {
    console.warn('WebSocket local configuration is incomplete. Please set VITE_LOCAL_WEBSOCKET_URL environment variable for development/testing.')
  }

  return undefined
}

export function getWebsocketConfig(): websocketConfig | undefined {
  const wsConfig: websocketConfig = {
    url: import.meta.env.VITE_WEBSOCKET_URL,
  }

  if (wsConfig.url) {
    return wsConfig
  } else {
    console.warn('WebSocket configuration is incomplete. Please set VITE_WEBSOCKET_URL environment variable.')
  }

  return getFallbackWebsocketConfig()
}

function getFallbackUrlsConfig(): UrlsConfig | undefined {
  const urlsConfig: UrlsConfig = {
    adminUrl: import.meta.env.VITE_LOCAL_ADMIN_URL,
    userUrl: import.meta.env.VITE_LOCAL_UI_PUBLIC_URL,
  }

  if (urlsConfig.adminUrl && urlsConfig.userUrl) {
    return urlsConfig
  } else {
    console.warn(
      'URLs local configuration is incomplete. Please set VITE_LOCAL_ADMIN_URL and VITE_LOCAL_UI_PUBLIC_URL environment variables for development/testing.',
    )
  }

  return undefined
}

export function getUrlsConfig(): UrlsConfig | undefined {
  const urlsConfig: UrlsConfig = {
    adminUrl: import.meta.env.VITE_ADMIN_URL,
    userUrl: import.meta.env.VITE_UI_PUBLIC_URL,
  }

  if (urlsConfig.adminUrl && urlsConfig.userUrl) {
    return urlsConfig
  } else {
    console.warn('URLs configuration is incomplete. Please set VITE_ADMIN_URL and VITE_UI_PUBLIC_URL environment variables.')
  }

  return getFallbackUrlsConfig()
}

export function getAwsConfig(): AwsConfig {
  const cognitoConfig = getCognitoConfig()

  if (!cognitoConfig) {
    console.warn('Aws configuration is incomplete. Please ensure all required environment variables are set.')
    throw new Error('Aws configuration is incomplete')
  }

  return {
    Auth: {
      Cognito: {
        userPoolId: cognitoConfig.userPoolId,
        userPoolClientId: cognitoConfig.userPoolClientId,
        identityPoolId: cognitoConfig.identityPoolId,
      },
    },
    region: cognitoConfig.region,
  }
}

export function getAppConfig(): AppConfig {
  const cognitoConfig = getCognitoConfig()
  const apiConfig = getApiConfig()
  const websocketConfig = getWebsocketConfig()

  if (!cognitoConfig || !apiConfig || !websocketConfig) {
    console.warn('App configuration is incomplete. Please ensure all required environment variables are set.')
    throw new Error('App configuration is incomplete')
  }

  return {
    cognito: cognitoConfig,
    api: apiConfig,
    websocket: websocketConfig,
  }
}
